import React from 'react';
import { Skeleton } from 'antd';

/**
 * SkeletonTable — placeholder beranimasi yang menyerupai tabel data.
 * Gunakan sebagai pengganti <Spin /> saat loadingData === true.
 *
 * Props:
 *   rows    — jumlah baris placeholder (default 6)
 *   columns — jumlah kolom placeholder (default 4)
 *   hasHeader — tampilkan baris header (default true)
 */
export default function SkeletonTable({ rows = 6, columns = 4, hasHeader = true }) {
	const colWidths = React.useMemo(() => {
		// Variasikan lebar kolom agar lebih natural
		const base = [60, 100, 140, 120, 80, 90, 110, 70];
		return Array.from({ length: columns }, (_, i) => base[i % base.length]);
	}, [columns]);

	return (
		<div style={{ padding: '0 0 8px' }}>
			{/* Header row */}
			{hasHeader && (
				<div style={{
					display        : 'flex',
					gap            : 16,
					padding        : '12px 16px',
					background     : 'linear-gradient(135deg, rgba(15,40,71,0.06), rgba(30,77,138,0.08))',
					borderBottom   : '2px solid #d0dff5',
					alignItems     : 'center',
				}}>
					{colWidths.map((w, i) => (
						<Skeleton.Input
							key={i}
							active
							style={{ width: w, height: 14, borderRadius: 6, flexShrink: 0 }}
						/>
					))}
				</div>
			)}

			{/* Data rows */}
			{Array.from({ length: rows }).map((_, rowIdx) => (
				<div
					key={rowIdx}
					style={{
						display      : 'flex',
						gap          : 16,
						padding      : '14px 16px',
						borderBottom : '1px solid #f3f4f6',
						alignItems   : 'center',
						background   : rowIdx % 2 === 1 ? '#fafafa' : '#fff',
					}}
				>
					{colWidths.map((w, colIdx) => (
						<Skeleton.Input
							key={colIdx}
							active
							style={{
								width       : w * (0.7 + Math.random() * 0.6),
								height      : 16,
								borderRadius: 6,
								flexShrink  : 0,
							}}
						/>
					))}
				</div>
			))}
		</div>
	);
}

/**
 * SkeletonCards — placeholder untuk tampilan card mobile.
 */
export function SkeletonCards({ rows = 4 }) {
	return (
		<div style={{ padding: '8px 0' }}>
			{Array.from({ length: rows }).map((_, i) => (
				<div
					key={i}
					style={{
						borderRadius : 14,
						border       : '1px solid #f0f0f0',
						marginBottom : 12,
						overflow     : 'hidden',
						background   : '#fff',
						boxShadow    : '0 2px 8px rgba(0,0,0,0.05)',
					}}
				>
					{/* Card header */}
					<div style={{
						padding        : '14px 16px',
						background     : 'linear-gradient(135deg, rgba(15,40,71,0.05), rgba(30,77,138,0.08))',
						borderBottom   : '1px solid #e8f0fb',
						display        : 'flex',
						gap            : 14,
						alignItems     : 'center',
					}}>
						<Skeleton.Avatar active size={44} shape="square" style={{ borderRadius: 10 }} />
						<div style={{ flex: 1 }}>
							<Skeleton.Input active style={{ width: '60%', height: 16, borderRadius: 6, marginBottom: 8, display: 'block' }} />
							<Skeleton.Input active style={{ width: '40%', height: 13, borderRadius: 5 }} />
						</div>
					</div>
					{/* Card body */}
					<div style={{ padding: '12px 16px', display: 'flex', gap: 10 }}>
						<Skeleton.Input active style={{ flex: 1, height: 13, borderRadius: 5 }} />
						<Skeleton.Input active style={{ flex: 1, height: 13, borderRadius: 5 }} />
					</div>
				</div>
			))}
		</div>
	);
}
